import { CodeNode, CodeHighlightNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode, $isListItemNode, $isListNode } from '@lexical/list';
import {
  TRANSFORMERS,
  type Transformer,
  type ElementTransformer,
  type TextMatchTransformer,
} from '@lexical/markdown';
import { createLinkMatcherWithRegExp } from '@lexical/react/LexicalAutoLinkPlugin';
import type { InitialConfigType } from '@lexical/react/LexicalComposer';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { $isElementNode } from 'lexical';
import type { ElementNode, LexicalNode, TextNode } from 'lexical';
import { ImageNode, $createImageNode, $isImageNode } from './ImageNode';

export const MARKDOWN_EDITOR_THEME = {
  paragraph: 'whitespace-pre-wrap text-sm',
  heading: {
    h1: 'text-2xl font-semibold mt-4 mb-2',
    h2: 'text-xl font-semibold mt-3 mb-2',
    h3: 'text-lg font-semibold mt-2 mb-1',
  },
  quote: 'border-l-2 border-neutral-300 pl-4 italic text-neutral-700',
  list: {
    nested: {
      listitem: '',
    },
    listitem:
      'text-sm leading-6 marker:font-normal marker:text-neutral-500 [&:has(>ul:only-child)]:list-none [&:has(>ol:only-child)]:list-none',
    ul: 'list-disc ml-6 [&>li]:my-1 [&>li>ul]:ml-6 [&>li>ol]:ml-6',
    ol: 'list-decimal ml-6 [&>li]:my-1 [&>li>ul]:ml-6 [&>li>ol]:ml-6',
  },
  code: 'block w-full whitespace-pre overflow-x-auto bg-neutral-900 text-neutral-50 border border-neutral-800 rounded-lg font-mono text-[13px] leading-6 px-3 py-2',
  link: 'text-blue-600 underline underline-offset-2',
  text: {
    bold: 'font-semibold',
    italic: 'italic',
    boldItalic: 'font-semibold italic',
  },
};

export const MARKDOWN_EDITOR_NODES = [
  HeadingNode,
  QuoteNode,
  ListNode,
  ListItemNode,
  CodeNode,
  CodeHighlightNode,
  LinkNode,
  AutoLinkNode,
  ImageNode,
];

// Negative lookbehind (?<!\[) prevents matching images inside linked-image
// syntax [![alt](img)](href) — lets the LINK transformer handle those instead.
const IMAGE_IMPORT_RE = /(?<!\[)!\[([^\]]*)\]\(([^)\s]+?)(?:\s+"([^"]*)")?\)(?:\{([^}]*)\})?/;

function exportImageNode(node: LexicalNode): string | null {
  if (!$isImageNode(node)) return null;
  const image = node as unknown as ImageNode;
  const src = image.__src;
  if (!src) return null;
  const alt = image.__altText ?? '';
  const title = image.__title ?? '';
  const width = image.__width ?? null;
  const height = image.__height ?? null;

  const escapedAlt = String(alt ?? '').replace(/]/g, '\\]');
  const escapedTitle = title ? String(title).replace(/"/g, '\\"') : '';
  const titlePart = escapedTitle ? ` "${escapedTitle}"` : '';
  const encodedSrc = encodeURI(String(src).trim()).replace(/\)/g, '\\)');
  const base = `![${escapedAlt}](${encodedSrc}${titlePart})`;

  if (width == null && height == null) {
    return base;
  }

  const optionParts: string[] = [];
  if (width != null) optionParts.push(`width=${width}`);
  if (height != null) optionParts.push(`height=${height}`);
  const suffix = optionParts.length ? `{${optionParts.join(' ')}}` : '';
  return `${base}${suffix}`;
}

function parseImageOptions(options: string): {
  width: number | string | null;
  height: number | string | null;
} {
  let width: number | string | null = null;
  let height: number | string | null = null;
  if (options) {
    const parts = options
      .split(/\s+/)
      .map((part) => part.trim())
      .filter(Boolean);
    for (const part of parts) {
      const [key, rawValue] = part.split('=');
      if (!key || rawValue == null) continue;
      const value = Number.isNaN(Number(rawValue)) ? rawValue : Number(rawValue);
      if (key === 'width') width = value;
      else if (key === 'height') height = value;
    }
  }
  return { width, height };
}

/**
 * Image transformer using text-match (inline) approach.
 *
 * Images are handled as inline text-match transformers (like links) instead
 * of element transformers. This is critical because Firecrawl markdown often
 * contains multiple consecutive images on a single line (e.g. logo carousels):
 *   ![Google](url)![Dale Carnegie](url)![OpenAI](url)
 *
 * With an element transformer, only the first image would be processed and
 * the remaining text (containing more images) would be on a detached node,
 * causing "Expected node to have a parent" errors.
 *
 * Must be ordered BEFORE the LINK text-match transformer so that `![alt](url)`
 * is matched as an image rather than `[alt](url)` being matched as a link.
 */
export const IMAGE_MARKDOWN_TRANSFORMER: TextMatchTransformer = {
  dependencies: [ImageNode],
  export: exportImageNode,
  importRegExp: IMAGE_IMPORT_RE,
  regExp: new RegExp(IMAGE_IMPORT_RE.source + '$'),
  replace: (textNode: TextNode, match: RegExpMatchArray) => {
    const [, altText = '', srcMatch = '', titleMatch = '', options = ''] = match;
    if (!srcMatch) return;
    let resolvedSrc = srcMatch.trim();
    try {
      resolvedSrc = decodeURI(resolvedSrc);
    } catch {
      // leave original src if decode fails
    }
    const { width, height } = parseImageOptions(options);
    const imageNode = $createImageNode({
      src: resolvedSrc,
      altText: altText || null,
      title: titleMatch || null,
      width,
      height,
    });
    textNode.replace(imageNode);
  },
  trigger: ')',
  type: 'text-match',
};

/**
 * Export-only element transformer for ImageNodes that end up at the root level
 * (e.g. from previously saved articles or copy-paste). Ensures they still
 * export as markdown even though import now uses the text-match transformer.
 */
const IMAGE_ROOT_EXPORT_TRANSFORMER: ElementTransformer = {
  dependencies: [ImageNode],
  export: exportImageNode,
  regExp: /(?!x)x/, // Never matches on import — we use text-match for import
  replace: () => false,
  type: 'element',
};

export const createMarkdownTransformers = (): Transformer[] => {
  const transformersWithCustomListExport = TRANSFORMERS.map((transformer) => {
    if (transformer.type !== 'element' || typeof transformer.export !== 'function') {
      return transformer;
    }

    const dependencies = transformer.dependencies as ElementTransformer['dependencies'];
    const handlesListNodes = dependencies.includes(ListNode) && dependencies.includes(ListItemNode);

    if (!handlesListNodes) {
      return transformer;
    }

    return {
      ...transformer,
      export: (node, exportChildren) =>
        $isListNode(node)
          ? exportListWithNestedSupport(node, exportChildren, 0)
          : transformer.export(node, exportChildren),
    } as ElementTransformer;
  });

  // IMAGE_MARKDOWN_TRANSFORMER must come BEFORE the default LINK text-match
  // transformer so that `![alt](url)` is matched as an image, not a link.
  // IMAGE_ROOT_EXPORT_TRANSFORMER handles ImageNodes at the root level on export.
  return [
    IMAGE_MARKDOWN_TRANSFORMER,
    IMAGE_ROOT_EXPORT_TRANSFORMER,
    ...transformersWithCustomListExport,
  ];
};

const LIST_INDENT_SIZE = 4;

const exportListWithNestedSupport = (
  listNode: ListNode,
  exportChildren: (node: ElementNode) => string,
  depth: number,
): string => {
  const output: string[] = [];
  const children = listNode.getChildren();
  let index = 0;

  for (const listItemNode of children) {
    if (!$isListItemNode(listItemNode)) {
      continue;
    }

    const indent = ' '.repeat(depth * LIST_INDENT_SIZE);
    const listType = listNode.getListType();
    const prefix =
      listType === 'number'
        ? `${listNode.getStart() + index}. `
        : listType === 'check'
          ? `- [${listItemNode.getChecked() ? 'x' : ' '}] `
          : '- ';

    const textSegments: string[] = [];
    const nestedSegments: string[] = [];

    for (const childNode of listItemNode.getChildren()) {
      if ($isListNode(childNode)) {
        nestedSegments.push(exportListWithNestedSupport(childNode, exportChildren, depth + 1));
      } else if ($isElementNode(childNode)) {
        textSegments.push(exportChildren(childNode));
      } else {
        textSegments.push(childNode.getTextContent());
      }
    }

    const textContent = textSegments.join('').replace(/\s+$/g, '');

    if (textContent.length === 0 && nestedSegments.length > 0) {
      output.push(...nestedSegments);
      continue;
    }

    let line = `${indent}${prefix}${textContent}`;

    if (nestedSegments.length > 0) {
      line = `${line}\n${nestedSegments.join('\n')}`;
    }

    output.push(line);
    index++;
  }
  return output.join('\n');
};

export const createMarkdownUrlMatchers = () => [
  createLinkMatcherWithRegExp(/https?:\/\/[^\s<>")\]]+(?<![.,);:!?"'\]])/i, (text: string) => text),
  createLinkMatcherWithRegExp(
    /www\.[^\s<>")\]]+(?<![.,);:!?"'\]])/i,
    (text: string) => `https://${text}`,
  ),
  createLinkMatcherWithRegExp(
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?<![.,);:!?"'\]])/i,
    (text: string) => `mailto:${text}`,
  ),
];

export const createMarkdownEditorConfig = (
  namespace: string,
  onError: InitialConfigType['onError'],
): InitialConfigType => ({
  namespace,
  onError,
  theme: MARKDOWN_EDITOR_THEME,
  nodes: MARKDOWN_EDITOR_NODES,
});
